import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { motion } from "framer-motion";
import { Lock, LogIn, User } from "lucide-react";
import {
  selectIsAuthenticated,
  useAppSelector,
  useLoginMutation,
} from "../store";

/** Shared ease — smooth deceleration, no spring “bounce / settle” */
const easeOut = [0.25, 0.1, 0.25, 1];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.055,
      delayChildren: 0.08,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, ease: easeOut },
  },
};

const orbTransition = {
  duration: 10,
  repeat: Infinity,
  repeatType: "mirror",
  ease: "easeInOut",
};

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const rawFrom = location.state?.from?.pathname || "/dashboard";
  const from =
    !rawFrom || rawFrom === "/" || rawFrom === "/login"
      ? "/dashboard"
      : rawFrom;

  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const isAuthed = useAppSelector(selectIsAuthenticated);
  const [login, { isLoading: isSigningIn }] = useLoginMutation();

  useEffect(() => {
    if (isAuthed) {
      navigate(from, { replace: true });
    }
  }, [from, navigate, isAuthed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId.trim() || !password.trim()) {
      setError("Please enter your user ID and password.");
      return;
    }
    setError("");
    try {
      await login({
        username: userId.trim(),
        password,
      }).unwrap();
      navigate(from, { replace: true });
    } catch {
      setError("Sign in failed. Check your user ID and password.");
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-gray-50 py-8 sm:py-12 md:py-16 dark:bg-gray-950">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -left-32 top-1/4 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl will-change-[opacity] dark:bg-blue-500/12"
          animate={{ opacity: [0.35, 0.55, 0.35] }}
          transition={orbTransition}
        />
        <motion.div
          className="absolute -right-24 bottom-1/4 h-96 w-96 rounded-full bg-blue-900/15 blur-3xl will-change-[opacity] dark:bg-blue-400/8"
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{ ...orbTransition, duration: 13 }}
        />
        <motion.div
          className="absolute left-1/2 top-0 h-px w-[min(80%,48rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent dark:via-blue-400/30"
          initial={{ opacity: 0, scaleX: 0.4 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.65, ease: easeOut }}
        />
      </div>

      <div className="relative z-10 flex flex-1 items-center justify-center px-4 sm:px-6">
        <motion.div
          className="relative w-full max-w-4xl transform-gpu overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-[0_25px_50px_-12px_rgba(30,58,138,0.28),0_12px_24px_-8px_rgba(15,23,42,0.18)] ring-1 ring-black/5 md:rounded-3xl dark:border-gray-700/80 dark:bg-gray-900/80 dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.55)] dark:ring-white/10"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.58, ease: easeOut }}
        >
          <div className="flex min-h-[min(100%,620px)] flex-col md:min-h-[540px] lg:min-h-[600px] md:flex-row">
            <motion.aside
              className="relative flex flex-col justify-between bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 px-10 py-14 text-white md:w-[44%] md:py-16 lg:px-12 lg:py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.06, ease: easeOut }}
            >
              <div>
                <motion.img
                  src={`${process.env.PUBLIC_URL}/hibbert-logo-on-dark.svg`}
                  alt="Hibbert Group"
                  className="mb-8 h-9 w-auto max-w-[min(100%,240px)]"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08, duration: 0.45, ease: easeOut }}
                />
                <motion.div
                  className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-blue-100/90 shadow-sm backdrop-blur-sm"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12, duration: 0.4, ease: easeOut }}
                >
                  <i
                    className="fa-solid fa-shield-halved text-[0.75rem] text-amber-300 drop-shadow-[0_0_10px_rgba(252,211,77,0.45)]"
                    aria-hidden
                  />
                  Secure sign in
                </motion.div>
                <motion.h1
                  className="text-3xl font-semibold leading-tight tracking-tight lg:text-4xl"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.16, duration: 0.45, ease: easeOut }}
                >
                  Accounting System
                </motion.h1>
                <motion.p
                  className="mt-4 max-w-sm text-sm leading-relaxed text-blue-100/80"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.24, duration: 0.45, ease: easeOut }}
                >
                  Sign in to manage clients, billing, allocations, and reporting
                  for Hibbert Group.
                </motion.p>
              </div>
              <motion.p
                className="mt-10 text-xs text-blue-200/60 md:mt-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.32, duration: 0.45, ease: easeOut }}
              >
                © 2026 Hibbert Group
              </motion.p>
            </motion.aside>

            <div className="relative flex flex-1 flex-col justify-center border-t border-gray-200/80 bg-white px-6 py-10 md:border-l md:border-t-0 md:px-10 md:py-12 lg:px-14 dark:border-gray-700/80 dark:bg-gray-950">
              <motion.div
                className="relative mx-auto w-full max-w-md"
                variants={container}
                initial="hidden"
                animate="show"
              >
                <motion.div variants={item} className="pb-1">
                  <div className="mb-8 text-center md:text-left">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                      Welcome back
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Enter your credentials to continue
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.04, duration: 0.4, ease: easeOut }}
                >
                  <label
                    htmlFor="login-user-id"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    User ID
                  </label>
                  <div className="relative">
                    <User
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      aria-hidden
                    />
                    <input
                      id="login-user-id"
                      type="text"
                      autoComplete="username"
                      value={userId}
                      onChange={(e) => {
                        setUserId(e.target.value);
                        setError("");
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-gray-900 shadow-sm transition-all placeholder:text-gray-400 hover:border-blue-400/70 hover:shadow-md focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/25 dark:border-gray-300 dark:bg-white dark:text-gray-900 dark:placeholder:text-gray-400 dark:hover:border-blue-500/60 dark:focus:border-blue-600 dark:focus:ring-blue-500/25"
                      placeholder="Enter your user ID"
                    />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.4, ease: easeOut }}
                >
                  <label
                    htmlFor="login-password"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      aria-hidden
                    />
                    <input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-gray-900 shadow-sm transition-all placeholder:text-gray-400 hover:border-blue-400/70 hover:shadow-md focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/25 dark:border-gray-300 dark:bg-white dark:text-gray-900 dark:placeholder:text-gray-400 dark:hover:border-blue-500/60 dark:focus:border-blue-600 dark:focus:ring-blue-500/25"
                      placeholder="Enter your password"
                    />
                  </div>
                </motion.div>

                {error ? (
                  <motion.p
                    role="alert"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="text-sm text-red-600 dark:text-red-400"
                  >
                    {error}
                  </motion.p>
                ) : null}

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.16, duration: 0.4, ease: easeOut }}
                >
                  <motion.button
                    type="submit"
                    disabled={isSigningIn}
                    whileHover={{ scale: isSigningIn ? 1 : 1.02 }}
                    whileTap={{ scale: isSigningIn ? 1 : 0.985 }}
                    transition={{ type: "tween", duration: 0.15, ease: easeOut }}
                    className="app-btn-gloss w-full px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 dark:focus:ring-offset-gray-900"
                  >
                    <LogIn className="h-4 w-4" aria-hidden />
                    {isSigningIn ? "Signing in…" : "Sign in"}
                  </motion.button>
                </motion.div>
                  </form>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
